import { CardHightlight, DialogReceipt, TableRecipes } from "../../components";
import DialogObservation from "../../components/Dialog/Observation";
import { Vehicle } from "../../components/Table/Recipes/Table";
import { Button, Dialog, Notification, Spinner } from "@rio-cloud/rio-uikit";
import {
  createTransaction,
  deleteFile,
  deleteTransaction,
  editTransaction,
  getDetailTransaction,
  getDRESummary,
  getFile,
  getTableData,
} from "../../services";
import { ITransactionPayload } from "../../types";
import { formatFileName, formatValue, parseCurrencyBRL } from "../../utils";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FormState } from "src/components/Dialog/Receipt/form";
import { allowedTypes, MAX_SIZE } from "../../utils/file";
import dashboardService from "../../services/dashboard";

import { useAppDispatch } from "@store";
import { setEndDate, setStartDate, useGetStoredEndDate, useGetStoredStartDate } from "@store/date/dateSlice";
import { formatDateRange } from "../../utils/dateRange";

interface ITableData {
  id: string;
  transactionDate: string;
  amount: number;
  segmentBussinessName: string;
  clientName: string;
  vehiclePlate: string;
  categoryName: string;
  subcategoryName: string;
  taxPercentage: string;
  tripCode: string;
  requestNumber: string;
}

interface fileAdjustmentsProps {
  rowNumber?: number;
  columnName?: string;
  errorMessage?: string;
}

const RecipesScreen = () => {
  const dispatch = useAppDispatch();
  const PAGE_SIZE = 10;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openModal, setOpenModal] = useState(false);

  const startDate = useGetStoredStartDate();
  const endDate = useGetStoredEndDate();

  // Auto-open modal if add=receipt query param is present
  useEffect(() => {
    if (searchParams.get("add") === "receipt") {
      setOpenModal(true);
      searchParams.delete("add");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecipeRecords, setTotalRecipeRecords] = useState(0);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalRecipeAmount, setTotalRecipeAmount] = useState(0);
  const [tableData, setTableData] = useState<Vehicle[]>([]);
  const [selected, setSelected] = useState<FormState & { id: string }>();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmFileId, setDeleteConfirmFileId] = useState<string | null>(null);
  const [observation, setObservation] = useState({
    open: false,
    value: "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const [errorFile, setErrorFile] = useState("");
  const [fileAdjustments, setFileAdjustments] = useState<fileAdjustmentsProps[]>([]);
  const [loadFile, setLoadFile] = useState(false);

  const handleSave = async (data: FormState, callback?: () => void, dialogModal = true) => {
    const payload: ITransactionPayload = {
      amount: parseCurrencyBRL(data?.value),
      branchId: Number(data?.branch),
      categoryId: Number(data?.category),
      description: data?.observation,
      driverId: data?.driver.length > 0 ? data?.driver : null,
      vehicleId: data?.vehicle ? data?.vehicle : null,
      segmentBussinessId: Number(data?.segment),
      subcategoryId: Number(data?.subcategory),
      supplierClientId: Number(data?.supplier),
      taxPercentage: Number(data?.taxes),
      transactionDate: data?.date,
      tripCode: data.trip,
      transactionType: "RECEITA",
    };

    const messages = {
      success: selected ? "Receita editada com sucesso." : "Receita criada com sucesso.",
      error: selected ? "Não foi possível editar a receita." : "Não foi possível criar a receita.",
    };

    try {
      selected
        ? await editTransaction(selected.id, payload, file || undefined)
        : await createTransaction(payload, file || undefined);

      if (dialogModal) {
        setOpenModal(false);
      }
      Notification.success(messages.success);
      callback?.();
      fetchInitial();
    } catch (error) {
      Notification.error(messages.error);
    }
  };

  const toVehicleRow = (item: ITableData): Vehicle => ({
    id: item.id,
    date: moment(item.transactionDate).format("DD/MM/YYYY"),
    amount: item.amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
    segment: item.segmentBussinessName,
    customer: item.clientName,
    contract: item.tripCode,
    vehicle: item.vehiclePlate,
    registration: item.requestNumber,
    category: item.categoryName,
    subcategory: item.subcategoryName,
    taxes: `${item.taxPercentage}%`,
    branch: "",
    driver: [],
  });

  const deriveTotalRecords = (payload: any, fallback: number) => {
    const candidates = [
      payload?.totalElements,
      payload?.total,
      payload?.totalRecords,
      payload?.totalItems,
      payload?.totalReceitas,
      payload?.totalDespesas,
      payload?.meta?.totalElements,
      payload?.meta?.total,
      payload?.data?.totalElements,
      payload?.data?.total,
    ];

    const found = candidates.find((value) => typeof value === "number");

    return typeof found === "number" ? found : fallback;
  };

  const fetchTableData = useCallback(
    async (page = 0, append = false) => {
      const setLoading = append ? setIsLoadingMore : setIsLoadingTable;
      setLoading(true);

      try {
        const response = await getTableData(startDate, endDate, "RECEITA", {
          page,
          size: PAGE_SIZE,
        });

        const normalizedPayload = response?.data ?? response;
        const content = Array.isArray(normalizedPayload?.content) ? normalizedPayload.content : [];
        const formattedRows = content.map(toVehicleRow);

        setTableData((prev) => (append ? [...prev, ...formattedRows] : formattedRows));
        setTotalRecipeRecords(deriveTotalRecords(normalizedPayload, formattedRows.length));
        setCurrentPage(page);
      } catch (error) {
        Notification.error("Não foi possível carregar as receitas.");
      } finally {
        setLoading(false);
      }
    },
    [endDate, startDate]
  );

  const getDashboard = useCallback(async () => {
    const response = await getDRESummary(startDate, endDate);
    setTotalRecipeAmount(response.data.totalReceitas || 0);
  }, [endDate, startDate]);

  const fetchInitial = useCallback(async () => {
    await Promise.all([getDashboard(), fetchTableData(0)]);
  }, [fetchTableData, getDashboard]);

  const handleLoadMore = () => {
    if (isLoadingMore || tableData.length >= totalRecipeRecords) {
      return;
    }

    fetchTableData(currentPage + 1, true);
  };

  const handleDeleteRecipe = async (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteFileRecipe = async (id: string) => {
    setDeleteConfirmFileId(id);
  };

  const confirmDeleteFileRecipe = async () => {
    if (!deleteConfirmFileId) return;

    try {
      await deleteFile(deleteConfirmFileId);
      setDeleteConfirmFileId(null);
      Notification.success("Arquivo excluído com sucesso.");
      fetchInitial();
    } catch (error) {
      Notification.error("Não foi possível excluir o arquivo.");
    }
  };

  const confirmDeleteRecipe = async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteTransaction(deleteConfirmId);
      setDeleteConfirmId(null);
      Notification.success("Receita excluída com sucesso.");
      fetchInitial();
    } catch (error) {
      Notification.error("Não foi possível excluir a receita.");
    }
  };

  const getDownloadFile = async (transactionId: string) => {
    try {
      const response = await getFile(transactionId);

      window.open(response.data.downloadUrl, "_blank");
      Notification.success("Comprovante baixado com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível baixar o comprovante.");
    }
  };

  const handleEdit = async (id: string) => {
    try {
      const response = await getDetailTransaction(id);

      if (response?.data?.fileNameAttachmentUrl) {
        setFileName(response?.data?.fileNameAttachmentUrl);
      }

      const mount: FormState & { id: string } = {
        branch: response?.data?.branchId?.toString(),
        category: response?.data?.categoryId?.toString(),
        date: response?.data?.transactionDate,
        segment: response?.data?.segmentBussinessId?.toString(),
        driver: response?.data?.driverIds || (response?.data?.driverId ? [response?.data?.driverId] : []),
        subcategory: response?.data?.subcategoryId?.toString(),
        observation: response?.data?.description,
        supplier: response?.data?.supplierClientId?.toString(),
        taxes: response?.data?.taxPercentage,
        trip: response?.data?.tripCode,
        value: formatValue(response?.data?.amount, false),
        vehicle: response?.data?.vehicleId?.toString(),
        id: response.data.id,
      };
      setSelected(mount);
      setOpenModal(true);
    } catch (error) {
      Notification.error("Não foi possível obter os detalhes da receita.");
    }
  };

  const handleObservationDefault = () => {
    setObservation({
      open: false,
      value: "",
    });
  };

  const handleObservation = async (id: string) => {
    try {
      const response = await getDetailTransaction(id);
      setObservation({
        open: true,
        value: response?.data?.description,
      });
    } catch (error) {
      Notification.error("Não foi possível obter os detalhes da receita.");
      handleObservationDefault();
    }
  };

  const handleOnCLose = () => {
    setOpenModal(false);
    setSelected(undefined);
  };

  useEffect(() => {
    const loadData = async () => {
      setCurrentPage(0);
      setTableData([]);
      setTotalRecipeRecords(0);
      await fetchInitial();
    };

    loadData();
  }, [startDate, endDate, fetchInitial]);

  const validateFile = (file: File | null): string => {
    if (!file) return "";

    if (file.size > MAX_SIZE) {
      return "O arquivo excede o tamanho máximo de 10 MB.";
    }

    if (!allowedTypes.includes(file.type)) {
      return "Formato não permitido. Use XLS ou XLSX.";
    }

    return "";
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    const name = formatFileName(file?.name);
    const validation = validateFile(file);

    if (!!validation) {
      setErrorFile(validation);
      setFileName("");
      return;
    }

    if (file) {
      setLoadFile(true);
      setErrorFile("");
      setFileName(name);

      const renamedFile = new File([file], name, { type: file.type, lastModified: file.lastModified });

      setFile(renamedFile);
      const response = await dashboardService.uploadExpenseFile(renamedFile);
      setLoadFile(false);

      if (!response) {
        Notification.error("Erro ao anexar arquivo.");
        return;
      }

      if (response?.data?.errors.length) {
        Notification.error("O seu arquivo precisa de ajustes");
        setFileAdjustments(response.data.errors);
        return;
      }

      Notification.success("Arquivo anexado com sucesso.");
      setOpenModal(false);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const fileClear = () => {
    setFileName("");
    setFile(null);
    setFileAdjustments([]);
  };

  const getModalFileUpload = () => {
    return (
      <div className="form-group display-flex justify-content-center flex-column align-items-center padding-20">
        {loadFile ? <Spinner /> : null}

        {!loadFile ? (
          <Button
            onClick={handleButtonClick}
            iconName="rioglyph rioglyph-upload"
            className="rounded-small margin-right-5"
          >
            Anexar arquivo receita
          </Button>
        ) : null}

        {fileName && (
          <Button bsStyle={Button.MUTED} iconName="rioglyph rioglyph-remove-sign" iconRight onClick={fileClear}>
            {fileName}
          </Button>
        )}
        <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
        {errorFile && <p className="text-color-danger margin-0 margin-top-5">{errorFile}</p>}

        {fileAdjustments.length > 0 && (
          <div className="margin-top-20 width-100pct max-height-200px overflow-auto">
            <p>Erros encontrados no arquivo:</p>
            <table className="table table-striped table-bordered">
              <thead>
                <tr>
                  <th>Coluna</th>
                  <th>Linha</th>
                  <th>Mensagem de Erro</th>
                </tr>
              </thead>
              <tbody>
                {fileAdjustments.map((adjustment, index) => (
                  <tr key={index}>
                    <td>{adjustment.columnName}</td>
                    <td>{adjustment.rowNumber}</td>
                    <td>{adjustment.errorMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="padding-20 display-flex flex-column gap-20 height-100pct">
      <div className="display-flex width-100pct gap-50">
        <div className="width-50pct">
          <CardHightlight
            icon="rioglyph rioglyph-calendar"
            label="Período"
            value={formatDateRange(new Date(startDate + "T00:00:00"), new Date(endDate + "T00:00:00"))}
            direction="LEFT"
          />
        </div>
        <div className="width-50pct">
          <CardHightlight
            icon="rioglyph rioglyph-arrow-up text-color-rating-4"
            label="Valor total das receitas"
            value={totalRecipeAmount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
            direction="RIGHT"
          />
        </div>
      </div>
      <div className="flex-1-1 height-100pct">
        <TableRecipes
          data={tableData}
          totalData={totalRecipeRecords}
          startDate={startDate}
          endDate={endDate}
          handleStartDate={(date) => dispatch(setStartDate(date))}
          handleEndDate={(date) => dispatch(setEndDate(date))}
          viewType="TABLE"
          onLoadMore={handleLoadMore}
          handleRefresh={fetchInitial}
          handleDownload={() => console.log("DOWNLOAD")}
          handleCreate={() => {
            setFileName("");
            setOpenModal(true);
          }}
          handleDelete={handleDeleteRecipe}
          onDeleteFile={handleDeleteFileRecipe}
          onGetFile={getDownloadFile}
          handleEdit={handleEdit}
          handleObservation={handleObservation}
          isLoadingMore={isLoadingMore}
          isLoadingTable={isLoadingTable}
        />
      </div>
      {/* <DialogReceipt
                title={selected ? 'Editar receita' : "Nova receita"}
                show={openModal}
                onClose={handleOnCLose}
                onSave={handleSave}
                selected={selected}
                handleFile={(file) => setFile(file)}
                handleClearFile={() => setFile(null)}
                fileName={fileName}
                changeFileName={filename => setFileName(filename)}
            /> */}

      <Dialog
        show={openModal}
        title={selected ? "Editar receita" : "Nova receita"}
        body={getModalFileUpload()}
        onClose={() => {
          setOpenModal(false);
        }}
        className="exampleDialog"
        showCloseButton
        bsSize="md"
      />

      <DialogObservation
        onClose={handleObservationDefault}
        show={observation.open}
        title={"Observações"}
        value={observation.value}
      />

      <Dialog
        show={deleteConfirmId !== null}
        title={<span className="text-medium text-size-16">Excluir receita</span>}
        body={
          <div className="padding-20">
            <p>Tem certeza que deseja excluir esta receita?</p>
            <p className="text-color-danger">Esta ação não pode ser desfeita.</p>
          </div>
        }
        footer={
          <div className="display-flex gap-10 justify-content-end">
            <button className="btn btn-default" onClick={() => setDeleteConfirmId(null)}>
              Cancelar
            </button>
            <button className="btn btn-danger" onClick={confirmDeleteRecipe}>
              Excluir
            </button>
          </div>
        }
        onClose={() => setDeleteConfirmId(null)}
        className="exampleDialog"
        showCloseButton
        bsSize="sm"
      />

      <Dialog
        show={deleteConfirmFileId !== null}
        title={<span className="text-medium text-size-16">Excluir comprovante</span>}
        body={
          <div className="padding-20">
            <p>Tem certeza que deseja excluir este comprovante?</p>
            <p className="text-color-danger">Esta ação não pode ser desfeita.</p>
          </div>
        }
        footer={
          <div className="display-flex gap-10 justify-content-end">
            <button className="btn btn-default" onClick={() => setDeleteConfirmFileId(null)}>
              Cancelar
            </button>
            <button className="btn btn-danger" onClick={confirmDeleteFileRecipe}>
              Excluir
            </button>
          </div>
        }
        onClose={() => setDeleteConfirmFileId(null)}
        className="exampleDialog"
        showCloseButton
        bsSize="sm"
      />
    </div>
  );
};

export default RecipesScreen;
